// What a store calls its improvement plans. Stored data never changes — the
// category stays 'PIP' and the document type stays 'Performance Improvement
// Plan' — only the words people read. Some operators avoid "PIP" because team
// members hear it as "we're trying to let you go".
// Mirror of server/src/utils/improvementPlanTerm.js; keep the two in sync.

export type ImprovementPlanTermKey = 'PIP' | 'DAP'

export interface ImprovementPlanTerm {
  key: ImprovementPlanTermKey
  /** "PIP" / "DAP" */
  abbr: string
  /** "PIPs" / "DAPs" */
  abbrPlural: string
  /** "Performance Improvement Plan" / "Development Action Plan" */
  full: string
  /** "Performance Improvement Plans" / "Development Action Plans" */
  fullPlural: string
  /** "Performance Plan" / "Development Plan" — for tight labels */
  shortName: string
  /** "improvement plan" / "development plan" — plain words in sentences */
  plainName: string
  /** "Improvement Plan" / "Development Plan" — plain words in titles */
  plainTitle: string
}

export const IMPROVEMENT_PLAN_TERMS: Record<ImprovementPlanTermKey, ImprovementPlanTerm> = {
  PIP: {
    key: 'PIP',
    abbr: 'PIP',
    abbrPlural: 'PIPs',
    full: 'Performance Improvement Plan',
    fullPlural: 'Performance Improvement Plans',
    shortName: 'Performance Plan',
    plainName: 'improvement plan',
    plainTitle: 'Improvement Plan',
  },
  DAP: {
    key: 'DAP',
    abbr: 'DAP',
    abbrPlural: 'DAPs',
    full: 'Development Action Plan',
    fullPlural: 'Development Action Plans',
    shortName: 'Development Plan',
    plainName: 'development plan',
    plainTitle: 'Development Plan',
  },
}

/** The stored document type (and discipline-policy action) for an improvement plan. */
export const IMPROVEMENT_PLAN_TYPE = 'Performance Improvement Plan'

export function resolveImprovementPlanTerm(settings: any): ImprovementPlanTerm {
  const key = settings?.userAccess?.documentation?.improvementPlanTerm
  return IMPROVEMENT_PLAN_TERMS[key as ImprovementPlanTermKey] ?? IMPROVEMENT_PLAN_TERMS.PIP
}

/**
 * Display a stored document type, category, or discipline action in the
 * store's wording. Anything that isn't an improvement-plan value passes
 * through untouched.
 */
export function displayPlanLabel(value: string | undefined | null, term: ImprovementPlanTerm): string {
  if (!value) return value ?? ''
  if (value === IMPROVEMENT_PLAN_TYPE) return term.full
  if (value === 'PIP') return term.abbr
  return value
}

/**
 * Rewrite free text (help guides, tour copy) into the store's wording.
 * A no-op for PIP stores, so their copy is byte-for-byte unchanged.
 */
export function applyPlanTerm(text: string, term: ImprovementPlanTerm): string {
  if (!text || term.key === 'PIP') return text
  const lower = (s: string) => s.toLowerCase()
  return text
    .replace(/Performance Improvement Plans/g, term.fullPlural)
    .replace(/Performance Improvement Plan/g, term.full)
    .replace(/performance improvement plans/g, lower(term.fullPlural))
    .replace(/performance improvement plan/g, lower(term.full))
    .replace(/Performance Plans/g, `${term.shortName}s`)
    .replace(/Performance Plan/g, term.shortName)
    .replace(/\bPIPs\b/g, term.abbrPlural)
    .replace(/\bPIP\b/g, term.abbr)
}
